/**
 * `HttpPort` binding over the global `fetch`, with the C1.4-pinned ENFORCED timeout.
 *
 * WHY THE TIMEOUT IS LOAD-BEARING (ports.ts caller obligation): `fetch` MUST always
 * settle. A never-settling request wedges the single-flight outbox drain for the process
 * lifetime — its pass awaits the in-flight request and only a restart recovers. So every
 * request is bounded: it settles by `timeoutMs` no matter what the transport does.
 *
 * HOW: we `Promise.race` the fetch against a timer that REJECTS, and also fire an
 * `AbortController` (best-effort). The race is the guarantee; the abort is the courtesy.
 * That distinction matters on native — see the CapacitorHttp note below: the native
 * bridge historically ignores the abort signal, so relying on abort alone would NOT
 * satisfy the obligation. The rejecting race does. A timed-out native request may keep
 * running in the background; that is harmless — the server dedups on `client_uuid`, so a
 * late-landing duplicate send converges (the whole outbox design), and the engine is
 * already unwedged.
 *
 * CLASSIFICATION (verified against transport/api.ts `attemptStep`): any REJECTION from
 * `http.fetch` classifies as `retryable` ("network error") — which is exactly the drain
 * policy's treatment (STOP the pass, backoff retry). A timeout rejecting is therefore
 * indistinguishable from a dropped connection to the drain, which is the intended
 * behavior: a slow/dead network is retried, never marked failed. This binding must NOT
 * resolve a synthetic error response on timeout (a resolved non-2xx would be misclassified).
 *
 * COOKIES / CapacitorHttp (capacitor.config.ts: `CapacitorHttp.enabled = true`): the app
 * is cookie-auth (HttpOnly `Maper` cookie; every app axios call sets `withCredentials:
 * true`, no Bearer header). On native, Capacitor patches the global `fetch` to route
 * through the native HTTP stack + native cookie jar, so the cookie is attached
 * regardless. On web, `fetch` defaults to `credentials: 'same-origin'`, which DROPS the
 * cookie for a cross-origin backend (a dev UI server on a different origin than the API,
 * or a split deploy) — so this binding sets `credentials: 'include'` to match the app's
 * `withCredentials: true`. There is one auth path (not a chat-specific one); this binding
 * only opts the browser `fetch` into sending it. (Cross-origin still requires the backend
 * to allow credentialed CORS + a `SameSite=None; Secure` cookie — the app already relies
 * on that for its axios calls.)
 */

import type { HttpPort, HttpRequest, HttpResponse } from '../core/ports';

/** Default request timeout — generous enough for a slow S3 media PUT, bounded enough to unwedge. */
export const DEFAULT_HTTP_TIMEOUT_MS = 30_000;

/** Rejection thrown when a request exceeds its timeout (classifies as a retryable network error). */
export class HttpTimeoutError extends Error {
    readonly timeoutMs: number;

    constructor(timeoutMs: number) {
        super(`chat HTTP request exceeded ${timeoutMs}ms timeout`);
        this.name = 'HttpTimeoutError';
        this.timeoutMs = timeoutMs;
    }
}

/**
 * The subset of `fetch` this binding uses. Returns `HttpResponse` (the port's shape) —
 * the DOM `Response` structurally satisfies it (status/ok/headers.get/json/text/
 * arrayBuffer), so the global `fetch` is assignable and tests can supply a plain fake.
 */
export type FetchLike = (input: string, init?: RequestInit) => Promise<HttpResponse>;

export interface HttpBindingOptions {
    /** Request timeout in ms (default 30 000). Every request settles by this deadline. */
    timeoutMs?: number;
    /** Injectable fetch for tests; defaults to the global `fetch`. */
    fetchImpl?: FetchLike;
    /**
     * `fetch` credentials mode (default `'include'` — the app's cookie-auth parity, unchanged).
     * The COCKPIT overrides this to `'omit'`: its panes carry EXPLICIT cast tokens, and the
     * server's CookieAuthMiddleware promotes an `access_token` cookie to Authorization only when
     * the header is ABSENT (login/middleware.py) — so a header-less pane request served same-site
     * as the API would silently authenticate AS THE OPERATOR (identity contamination). Pinning
     * `'omit'` makes the explicit token the ONLY identity, failing loudly instead. See
     * `createCockpitHttpPort` (chat/cockpit/tokenHttpPort.ts).
     */
    credentials?: RequestCredentials;
}

/**
 * Build the `HttpPort` binding. Pure factory — no Capacitor import (CapacitorHttp patches
 * the global `fetch` at runtime; this file is agnostic to that), so it is import-safe and
 * Node-testable with an injected `fetchImpl`.
 */
export function createHttpPort(options: HttpBindingOptions = {}): HttpPort {
    const timeoutMs = options.timeoutMs ?? DEFAULT_HTTP_TIMEOUT_MS;
    const fetchImpl: FetchLike = options.fetchImpl ?? ((input, init) => fetch(input, init));
    const credentials: RequestCredentials = options.credentials ?? 'include';

    return {
        async fetch(request: HttpRequest): Promise<HttpResponse> {
            const controller = new AbortController();
            const limitMs = request.timeoutMs ?? timeoutMs;
            let timer: ReturnType<typeof setTimeout> | undefined;
            let cancel: (() => void) | undefined;
            // Rejects on the deadline and on the caller's abort. CapacitorHttp ignores the abort
            // signal, so the race is what makes either one settle the request.
            const settled = new Promise<never>((_resolve, reject) => {
                if (limitMs > 0) {
                    timer = setTimeout(() => {
                        controller.abort();
                        reject(new HttpTimeoutError(limitMs));
                    }, limitMs);
                }
                cancel = () => {
                    controller.abort();
                    reject(request.signal?.reason ?? new DOMException('Aborted', 'AbortError'));
                };
                if (request.signal?.aborted) cancel();
                else request.signal?.addEventListener('abort', cancel);
            });

            const init: RequestInit = {
                method: request.method,
                headers: request.headers,
                body: request.body as BodyInit | undefined,
                signal: controller.signal,
                // Default `'include'`: send the auth cookie. `fetch` defaults to `same-origin`,
                // which drops the cookie for a cross-origin backend (a dev UI server ≠ the API
                // origin) — parity with the app's axios `withCredentials: true` (cookie-auth, no
                // Bearer header). Without it, every chat request 401s cross-origin. The cockpit
                // pins `'omit'` (explicit-token panes must never ride the operator cookie — B5).
                credentials: request.credentials ?? credentials,
            };

            try {
                // The race is the always-settles guarantee; the fetch Response already
                // structurally satisfies HttpResponse (status/ok/headers.get/json/text/
                // arrayBuffer), so no adapter shim is needed.
                return await Promise.race([fetchImpl(request.url, init), settled]);
            } finally {
                if (timer !== undefined) {
                    clearTimeout(timer);
                }
                if (cancel) request.signal?.removeEventListener('abort', cancel);
            }
        },
    };
}
